import React, { useEffect, useState, useCallback } from 'react';
import { placeLabel } from '../lib/placeLabel';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { toast } from 'sonner';
import { FileText, Search, Download, Send, CheckCircle2, Undo2, PoundSterling, AlertCircle, Plus, MapPin, Trash2, Clock, Pencil } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import AdminLayout from '../components/AdminLayout';
import { formatUkDate } from '../lib/date';
import { computeEndTime } from '../lib/time';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_LIBRARIES = ['places'];
const UNIT_OPTIONS = [
  { value: 'none', label: '—' },
  { value: 'hours', label: 'Hours' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'miles', label: 'Miles' },
  { value: 'stops', label: 'Stops' },
  { value: 'journey', label: 'Journey' },
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
  { value: 'flat', label: 'Flat fee' },
];

// Mirror the public website + Dispatch admin form
const SERVICE_TYPES = [
  { id: 'Transfer', name: 'Transfer (Point-to-Point / Airport)' },
  { id: 'As Directed Service', name: 'As Directed Service (Hourly)' },
  { id: 'Wedding Chauffeur', name: 'Wedding Chauffeur' },
  { id: 'Corporate / Roadshow', name: 'Corporate / Roadshow' },
  { id: 'Event Transport', name: 'Event Transport' },
  { id: 'Chauffeur service', name: 'Other (Chauffeur service)' },
];

const VEHICLES = [
  { id: 'Mercedes-Benz V Class Exclusive', name: 'Mercedes-Benz V Class Exclusive', meta: 'Luxury MPV · 6 PAX' },
  { id: 'Mercedes-Benz V Class Executive', name: 'Mercedes-Benz V Class Executive', meta: 'Business MPV · 7 PAX' },
  { id: 'Mercedes-Benz E Class', name: 'Mercedes-Benz E Class', meta: 'Executive Saloon · 3 PAX' },
  { id: 'Mercedes-Benz S Class', name: 'Mercedes-Benz S Class', meta: 'Luxury Saloon · 3 PAX' },
  { id: 'Range Rover Velar', name: 'Range Rover Velar', meta: 'Executive SUV · 3 PAX' },
];

const STATUS_PILL = {
  draft:     { label: 'Draft',     cls: 'bg-blue-100 text-blue-800' },
  unpaid:    { label: 'Unpaid',    cls: 'bg-amber-100 text-amber-800' },
  paid:      { label: 'Paid',      cls: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700' },
};

// Small presentational helpers used by the New Invoice dialog
const SectionLabel = ({ n, title, optional = false }) => (
  <div className="flex items-baseline gap-3 pt-1">
    <span className="text-[10px] font-semibold text-gray-400 tabular-nums tracking-widest">{n}</span>
    <h3 className="text-[11px] tracking-[0.3em] uppercase font-semibold text-black">{title}</h3>
    {optional && <span className="text-[10px] text-gray-400 italic">— optional</span>}
    <span className="flex-1 border-t border-gray-200 ml-2" />
  </div>
);

const FieldShell = ({ label, icon: Icon, children, className = '' }) => (
  <div className={className}>
    <label className="block text-[10px] tracking-[0.18em] uppercase font-semibold text-gray-500 mb-1">
      {label}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute left-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none z-10" />}
      {children}
    </div>
  </div>
);


const Invoices = () => {
  const [data, setData] = useState({ invoices: [], summary: { total: 0, unpaid: 0, paid: 0, outstandingAmount: 0 } });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    customerName: '', customerEmail: '', customerPhone: '', customerAddress: '',
    companyName: '', billingAddress: '',
    customerVatNumber: '', purchaseOrderNumber: '', invoiceContactName: '', costReference: '',
    serviceDescription: 'Transfer', vehicleName: 'Mercedes-Benz V Class Exclusive',
    pickupLocation: '', dropoffLocation: '', pickupDate: '', pickupTime: '', endTime: '',
    passengerName: '', hours: '', sendEmail: true, customRef: '',
  });
  const [lineItems, setLineItems] = useState([{ description: '', quantity: '1', unit: '', unitPrice: '' }]);
  const [loadRef, setLoadRef] = useState('');
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Google Maps autocomplete refs for the manual invoice journey fields
  const { isLoaded: gmapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GMAPS_LIBRARIES,
  });
  const [pickupAC, setPickupAC] = useState(null);
  const [dropoffAC, setDropoffAC] = useState(null);
  const [addressAC, setAddressAC] = useState(null);

  const onPickupPlace = () => {
    if (pickupAC) {
      const p = pickupAC.getPlace();
      const addr = placeLabel(p);
      if (addr) setManualForm((prev) => ({ ...prev, pickupLocation: addr }));
    }
  };
  const onDropoffPlace = () => {
    if (dropoffAC) {
      const p = dropoffAC.getPlace();
      const addr = placeLabel(p);
      if (addr) setManualForm((prev) => ({ ...prev, dropoffLocation: addr }));
    }
  };
  const onAddressPlace = () => {
    if (addressAC) {
      const p = addressAC.getPlace();
      const addr = placeLabel(p);
      if (addr) setManualForm((prev) => ({ ...prev, customerAddress: addr }));
    }
  };

  const resetManualForm = () => {
    setManualForm({
      customerName: '', customerEmail: '', customerPhone: '', customerAddress: '',
      companyName: '', billingAddress: '',
      customerVatNumber: '', purchaseOrderNumber: '', invoiceContactName: '', costReference: '',
      serviceDescription: 'Transfer', vehicleName: 'Mercedes-Benz V Class Exclusive',
      pickupLocation: '', dropoffLocation: '', pickupDate: '', pickupTime: '', endTime: '',
      passengerName: '', hours: '', sendEmail: true, customRef: '',
    });
    setLineItems([{ description: '', quantity: '1', unit: '', unitPrice: '' }]);
    setLoadRef('');
    setEditingId(null);
  };

  // Open the dialog pre-loaded from an existing (draft) invoice to price/edit it
  const editInvoice = (inv) => {
    setEditingId(inv.id);
    setManualForm({
      customerName: inv.customerName || '', customerEmail: inv.customerEmail || inv.billingEmail || '',
      customerPhone: inv.customerPhone || '', customerAddress: inv.customerAddress || inv.billingAddress || '',
      companyName: inv.companyName || '', billingAddress: inv.billingAddress || '',
      customerVatNumber: inv.customerVatNumber || '', purchaseOrderNumber: inv.purchaseOrderNumber || '',
      invoiceContactName: inv.invoiceContactName || '', costReference: inv.costReference || '',
      serviceDescription: inv.serviceType || 'Transfer', vehicleName: inv.vehicleName || 'Mercedes-Benz V Class Exclusive',
      pickupLocation: inv.pickupLocation || '', dropoffLocation: inv.dropoffLocation || '',
      pickupDate: inv.pickupDate || '', pickupTime: inv.pickupTime || '', endTime: inv.endTime || '',
      passengerName: inv.passengerName || '', hours: inv.hours ? String(inv.hours) : '',
      sendEmail: true, customRef: inv.bookingId || '',
    });
    setLineItems((inv.lineItems || []).map((it) => ({
      description: it.description || '', quantity: String(it.quantity ?? 1),
      unit: it.unit || '', unitPrice: (it.unitPrice ?? '') === '' ? '' : String(it.unitPrice),
    })).concat((inv.lineItems || []).length ? [] : [{ description: '', quantity: '1', unit: '', unitPrice: '' }]));
    setShowManual(true);
  };

  // ---- Line item helpers --------------------------------------------------
  const lineAmount = (it) => {
    const q = parseFloat(it.quantity);
    const p = parseFloat(it.unitPrice);
    if (Number.isNaN(q) || Number.isNaN(p)) return 0;
    return Math.round(q * p * 100) / 100;
  };
  const lineItemsTotal = lineItems.reduce((sum, it) => sum + lineAmount(it), 0);
  const addLineItem = () => setLineItems((p) => [...p, { description: '', quantity: '1', unit: '', unitPrice: '' }]);
  const removeLineItem = (i) => setLineItems((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));
  const updateLineItem = (i, k, v) => setLineItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  // Load a draft (customer + journey + suggested line items) from a real booking
  const loadFromBooking = useCallback(async (refArg) => {
    const ref = (refArg || loadRef || '').trim();
    if (!ref) { toast.error('Enter a booking reference'); return; }
    setLoadingDraft(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/bookings/${ref}/invoice-draft`);
      setManualForm((prev) => ({
        ...prev,
        customerName: d.customerName || '', customerEmail: d.customerEmail || '',
        customerPhone: d.customerPhone || '', customerAddress: d.customerAddress || '',
        companyName: d.companyName || '', billingAddress: d.billingAddress || '',
        customerVatNumber: d.customerVatNumber || '', purchaseOrderNumber: d.purchaseOrderNumber || '',
        invoiceContactName: d.invoiceContactName || '', costReference: d.costReference || '',
        serviceDescription: d.serviceDescription || 'Transfer', vehicleName: d.vehicleName || prev.vehicleName,
        pickupLocation: d.pickupLocation || '', dropoffLocation: d.dropoffLocation || '',
        pickupDate: d.pickupDate || '', pickupTime: d.pickupTime || '', endTime: d.endTime || '',
        passengerName: d.passengerName || '', hours: d.hours ? String(d.hours) : '', customRef: d.customRef || ref,
      }));
      setLineItems((d.lineItems || []).map((it) => ({
        description: it.description || '', quantity: String(it.quantity ?? 1),
        unit: it.unit || '', unitPrice: it.unitPrice ? String(it.unitPrice) : '',
      })).concat((d.lineItems || []).length ? [] : [{ description: '', quantity: '1', unit: '', unitPrice: '' }]));
      toast.success(`Loaded ${ref}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking not found');
    } finally {
      setLoadingDraft(false);
    }
  }, [loadRef]);

  // Deep link: /admin/invoices?booking=OC-XXXX opens the dialog pre-loaded
  useEffect(() => {
    const ref = searchParams.get('booking');
    if (ref) {
      setShowManual(true);
      loadFromBooking(ref);
      searchParams.delete('booking');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadFromBooking]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (query.trim()) params.q = query.trim();
      const { data } = await axios.get(`${API}/admin/invoices`, { params });
      setData(data);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // debounced search + filter changes
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, query]);

  const act = async (invoice, type) => {
    const labels = {
      'mark-paid':     `Mark ${invoice.id} as paid?`,
      'mark-unpaid':   `Mark ${invoice.id} as unpaid?`,
      'send-reminder': `Send a payment reminder for ${invoice.id}?`,
      'resend':        `Re-send ${invoice.id} to ${invoice.billingEmail || invoice.customerEmail}?`,
    };
    if (!window.confirm(labels[type])) return;
    setBusyId(invoice.id);
    try {
      const body = type === 'mark-paid' ? { paymentMethod: 'Bank transfer' } : {};
      await axios.post(`${API}/admin/invoices/${invoice.id}/${type}`, body);
      const toasts = {
        'mark-paid': 'Marked paid · receipt sent',
        'mark-unpaid': 'Marked unpaid',
        'send-reminder': 'Reminder queued',
        'resend': 'Invoice re-sent',
      };
      toast.success(toasts[type]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId('');
    }
  };

  const downloadPdf = (invoiceId) => {
    window.open(`${API}/admin/invoices/${invoiceId}/pdf`, '_blank');
  };

  const submitManual = async (sendAfter = false) => {
    if (!manualForm.customerName || !manualForm.customerEmail) {
      toast.error('Customer name and email are required');
      return;
    }
    const items = lineItems
      .filter((it) => it.description.trim() && (parseFloat(it.unitPrice) || 0) >= 0)
      .map((it) => ({
        description: it.description.trim(),
        quantity: parseFloat(it.quantity) || 0,
        unit: it.unit || null,
        unitPrice: parseFloat(it.unitPrice) || 0,
      }));
    if (items.length === 0) {
      toast.error('Add at least one line item with a description');
      return;
    }
    if (lineItemsTotal <= 0) {
      toast.error('Total must be greater than zero');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        // Price/update an existing (draft) invoice
        await axios.put(`${API}/admin/invoices/${editingId}`, {
          customerName: manualForm.customerName, customerEmail: manualForm.customerEmail,
          customerPhone: manualForm.customerPhone, customerAddress: manualForm.customerAddress,
          companyName: manualForm.companyName,
          customerVatNumber: manualForm.customerVatNumber, purchaseOrderNumber: manualForm.purchaseOrderNumber,
          invoiceContactName: manualForm.invoiceContactName, costReference: manualForm.costReference,
          serviceType: manualForm.serviceDescription, vehicleName: manualForm.vehicleName,
          pickupLocation: manualForm.pickupLocation, dropoffLocation: manualForm.dropoffLocation,
          pickupDate: manualForm.pickupDate, pickupTime: manualForm.pickupTime, endTime: manualForm.endTime,
          passengerName: manualForm.passengerName,
          hours: manualForm.hours ? parseFloat(manualForm.hours) : null,
          lineItems: items,
        });
        if (sendAfter) {
          await axios.post(`${API}/admin/invoices/${editingId}/finalise`);
          toast.success(`Invoice ${editingId} sent to the customer`);
        } else {
          toast.success(`Draft ${editingId} saved`);
        }
      } else {
        const payload = {
          ...manualForm,
          hours: manualForm.hours ? parseFloat(manualForm.hours) : null,
          amount: lineItemsTotal,
          lineItems: items,
        };
        const { data: res } = await axios.post(`${API}/admin/invoices/manual`, payload);
        toast.success(`Invoice ${res.invoice.id} created${manualForm.sendEmail ? ' & emailed' : ''}`);
      }
      setShowManual(false);
      resetManualForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save invoice');
    } finally {
      setSubmitting(false);
    }
  };

  // List-row action: send a ready (fully-priced) draft straight away
  const sendDraft = async (inv) => {
    if (inv.needsPricing) { editInvoice(inv); return; }
    if (!window.confirm(`Send invoice ${inv.id} (£${Number(inv.amount || 0).toFixed(2)}) to ${inv.customerEmail || inv.billingEmail}?`)) return;
    setBusyId(inv.id);
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/finalise`);
      toast.success(`Invoice ${inv.id} sent`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invoice');
    } finally {
      setBusyId('');
    }
  };

  const deleteDraft = async (inv) => {
    if (!window.confirm(`Delete draft invoice ${inv.id}? This cannot be undone.`)) return;
    setBusyId(inv.id);
    try {
      await axios.delete(`${API}/admin/invoices/${inv.id}`);
      toast.success(`Draft ${inv.id} deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete draft');
    } finally {
      setBusyId('');
    }
  };

  const updateField = (k, v) => setManualForm((p) => ({ ...p, [k]: v }));

  const StatCard = ({ icon: Icon, label, value, testId }) => (
    <Card className="p-4 flex items-center gap-3" data-testid={testId}>
      <div className="h-9 w-9 rounded-lg bg-black text-white flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
        <p className="text-xl font-bold text-black leading-tight">{value}</p>
      </div>
    </Card>
  );

  const invoices = data.invoices || [];

  return (
    <AdminLayout title="Invoices" subtitle="Corporate billing — track payments, reminders and receipts">
      <div className="space-y-5">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={FileText}        label="Invoices"    value={data.summary.total} testId="inv-stat-total" />
          <StatCard icon={Pencil}          label="Drafts"      value={data.summary.draft || 0} testId="inv-stat-draft" />
          <StatCard icon={AlertCircle}     label="Unpaid"      value={data.summary.unpaid} testId="inv-stat-unpaid" />
          <StatCard icon={Clock}           label="Overdue"     value={data.summary.overdue || 0} testId="inv-stat-overdue" />
          <StatCard icon={PoundSterling}   label="Outstanding" value={`£${(data.summary.outstandingAmount || 0).toFixed(2)}`} testId="inv-stat-outstanding" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 border-b border-gray-200" data-testid="invoices-tabs">
          {[
            { id: 'all', label: 'All', count: data.summary.total },
            { id: 'draft', label: 'Drafts', count: data.summary.draft },
            { id: 'unpaid', label: 'Unpaid', count: data.summary.unpaid },
            { id: 'overdue', label: 'Overdue', count: data.summary.overdue },
            { id: 'paid', label: 'Paid', count: data.summary.paid },
            { id: 'cancelled', label: 'Cancelled', count: data.summary.cancelled },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${statusFilter === t.id ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'}`}
              data-testid={`invoices-tab-${t.id}`}
            >
              {t.label}
              {typeof t.count === 'number' && t.count > 0 && (
                <span className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full ${statusFilter === t.id ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Card className="p-3 sm:p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by invoice ID, company, customer, PO, cost ref…"
                value={query} onChange={(e) => setQuery(e.target.value)}
                className="pl-10 bg-white h-10" data-testid="invoices-search"
              />
            </div>
            <Button
              onClick={() => { resetManualForm(); setShowManual(true); }}
              className="bg-black text-white hover:bg-gray-800 h-10"
              data-testid="new-invoice-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New Invoice
            </Button>
          </div>
        </Card>

        {/* List */}
        {loading ? (
          <Card className="p-6 text-sm text-gray-600">Loading…</Card>
        ) : invoices.length === 0 ? (
          <Card className="p-10 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium">No invoices match your filters.</p>
            <p className="text-xs text-gray-500 mt-1">Invoices are created automatically when a corporate customer accepts their quote.</p>
          </Card>
        ) : (
          <Card className="overflow-hidden p-0" data-testid="invoices-list">
            <div className="hidden md:grid grid-cols-[1.4fr,1.4fr,0.9fr,0.7fr,0.9fr,1.5fr] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wide font-semibold text-gray-500">
              <div>Invoice</div><div>Company / Customer</div><div className="text-right">Amount</div><div>Status</div><div>Issued</div><div className="text-right">Actions</div>
            </div>
            <ul className="divide-y divide-gray-100">
              {invoices.map((inv) => {
                const pill = STATUS_PILL[inv.status] || STATUS_PILL.unpaid;
                return (
                  <li key={inv.id} className="px-5 py-4 grid grid-cols-1 md:grid-cols-[1.4fr,1.4fr,0.9fr,0.7fr,0.9fr,1.5fr] gap-3 md:gap-4 items-center" data-testid={`invoice-row-${inv.id}`}>
                    <div className="min-w-0">
                      <p className="font-bold text-black font-mono text-sm truncate">{inv.id}</p>
                      <p className="text-xs text-gray-500 truncate">Booking {inv.bookingId}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-black truncate">{inv.companyName || '—'}</p>
                      <p className="text-xs text-gray-500 truncate">{inv.customerName} · {inv.billingEmail || inv.customerEmail}</p>
                    </div>
                    <div className="md:text-right">
                      <p className="font-bold text-black tabular-nums">£{Number(inv.amount || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${pill.cls}`}>{pill.label}</span>
                      {inv.overdue && (
                        <span className="ml-1.5 px-2 py-1 rounded-full text-[10px] font-semibold bg-red-100 text-red-700" data-testid={`invoice-overdue-${inv.id}`}>Overdue</span>
                      )}
                      {inv.status === 'draft' && inv.needsPricing && (
                        <span className="ml-1.5 px-2 py-1 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">Needs pricing</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {formatUkDate(inv.issuedAt)}
                      {inv.remindersSent > 0 && (
                        <p className="text-[10px] text-amber-600 mt-0.5">{inv.remindersSent} reminder{inv.remindersSent === 1 ? '' : 's'} sent</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 md:justify-end">
                      <Button size="sm" variant="outline" className="border-gray-300 h-8 px-2.5" onClick={() => downloadPdf(inv.id)} data-testid={`invoice-pdf-${inv.id}`}>
                        <Download className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">PDF</span>
                      </Button>
                      {inv.status === 'draft' && (
                        <>
                          <Button size="sm" variant="outline" className="border-gray-300 h-8 px-2.5" onClick={() => editInvoice(inv)} data-testid={`invoice-edit-${inv.id}`}>
                            <Pencil className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">{inv.needsPricing ? 'Price' : 'Edit'}</span>
                          </Button>
                          <Button size="sm" className="bg-black hover:bg-gray-800 text-white h-8 px-2.5" onClick={() => sendDraft(inv)} disabled={busyId === inv.id} data-testid={`invoice-send-${inv.id}`}>
                            <Send className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">Send</span>
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 h-8 px-2.5" onClick={() => deleteDraft(inv)} disabled={busyId === inv.id} data-testid={`invoice-delete-${inv.id}`}>
                            <Trash2 className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">Delete</span>
                          </Button>
                        </>
                      )}
                      {inv.status === 'unpaid' && (
                        <>
                          <Button size="sm" variant="outline" className="border-gray-300 h-8 px-2.5" onClick={() => act(inv, 'send-reminder')} disabled={busyId === inv.id} data-testid={`invoice-remind-${inv.id}`}>
                            <Send className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">Remind</span>
                          </Button>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-2.5" onClick={() => act(inv, 'mark-paid')} disabled={busyId === inv.id} data-testid={`invoice-mark-paid-${inv.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">Mark paid</span>
                          </Button>
                        </>
                      )}
                      {inv.status === 'paid' && (
                        <Button size="sm" variant="outline" className="border-gray-300 h-8 px-2.5" onClick={() => act(inv, 'mark-unpaid')} disabled={busyId === inv.id} data-testid={`invoice-mark-unpaid-${inv.id}`}>
                          <Undo2 className="h-3.5 w-3.5 md:mr-1.5" /><span className="hidden md:inline text-xs">Undo paid</span>
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </div>

      {/* New Invoice dialog — sleek, full-bleed luxury redesign matching the PDF aesthetic */}
      <Dialog open={showManual} onOpenChange={(open) => { setShowManual(open); if (!open) resetManualForm(); }}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] p-0 overflow-hidden gap-0 border-0 rounded-none sm:rounded-none bg-white flex flex-col"
          data-testid="manual-invoice-dialog"
          // Keep Google Places autocomplete clickable inside the dialog
          onPointerDownOutside={(e) => {
            if (e.target?.closest?.('.pac-container')) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (e.target?.closest?.('.pac-container')) e.preventDefault();
          }}
        >
          {/* Solid black header — mirrors the PDF TOTAL DUE / table header bar */}
          <div className="bg-black text-white px-8 py-6 flex items-start justify-between">
            <div>
              <p className="text-[10px] tracking-[0.3em] font-semibold text-white/60 uppercase">Obsidian Chauffeurs</p>
              <DialogTitle className="text-3xl font-bold mt-1 tracking-tight">{editingId ? 'Edit / Price Invoice' : 'New Invoice'}</DialogTitle>
              <p className="text-xs text-white/60 mt-1.5 font-light">
                {editingId ? `${editingId} — set prices, then Save & Send.` : 'Off-platform billing — phone bookings, walk-ins, repeat clients.'}
              </p>
            </div>
            {/* Live amount preview, top-right */}
            <div className="text-right">
              <p className="text-[10px] tracking-[0.3em] font-semibold text-white/60 uppercase">Total</p>
              <p className="text-3xl font-bold mt-1 tabular-nums" data-testid="mi-total-preview">
                £{lineItemsTotal.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Load-from-booking bar (hidden when editing an existing invoice) */}
          {!editingId && (
          <div className="bg-neutral-900 text-white px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 shrink-0">Pre-fill from booking</span>
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={loadRef}
                onChange={(e) => setLoadRef(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadFromBooking(); }}
                placeholder="e.g. OC-C58FFA95E3BF"
                className="h-9 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-none focus-visible:ring-0 focus-visible:border-white"
                data-testid="mi-load-ref"
              />
              <Button
                onClick={() => loadFromBooking()}
                disabled={loadingDraft}
                className="bg-white text-black hover:bg-white/90 rounded-none h-9 px-4 text-xs uppercase tracking-[0.15em] font-semibold shrink-0"
                data-testid="mi-load-btn"
              >
                {loadingDraft ? 'Loading…' : 'Load'}
              </Button>
            </div>
          </div>
          )}

          {/* Scrollable body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-8 py-7 space-y-7">
            {/* === Customer ============================================== */}
            <SectionLabel n="01" title="Customer" />
            <div className="grid grid-cols-2 gap-x-5 gap-y-4">
              <FieldShell label="Full name *">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.customerName} onChange={(e) => updateField('customerName', e.target.value)} data-testid="mi-customer-name" />
              </FieldShell>
              <FieldShell label="Email *">
                <Input type="email" className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.customerEmail} onChange={(e) => updateField('customerEmail', e.target.value)} data-testid="mi-customer-email" />
              </FieldShell>
              <FieldShell label="Phone">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.customerPhone} onChange={(e) => updateField('customerPhone', e.target.value)} data-testid="mi-customer-phone" />
              </FieldShell>
              <FieldShell label="Address" icon={MapPin}>
                {gmapsLoaded ? (
                  <Autocomplete onLoad={setAddressAC} onPlaceChanged={onAddressPlace} options={{ componentRestrictions: { country: 'gb' } }}>
                    <Input className="h-11 border-0 border-b border-gray-300 rounded-none pl-6 focus-visible:ring-0 focus-visible:border-black bg-transparent" placeholder="Start typing address…" value={manualForm.customerAddress} onChange={(e) => updateField('customerAddress', e.target.value)} data-testid="mi-customer-address" />
                  </Autocomplete>
                ) : (
                  <Input className="h-11 border-0 border-b border-gray-300 rounded-none pl-6 focus-visible:ring-0 focus-visible:border-black bg-transparent" placeholder="Customer address" value={manualForm.customerAddress} onChange={(e) => updateField('customerAddress', e.target.value)} data-testid="mi-customer-address" />
                )}
              </FieldShell>
            </div>

            {/* === Corporate ============================================ */}
            <SectionLabel n="02" title="Corporate billing" optional />
            <div className="grid grid-cols-2 gap-x-5 gap-y-4">
              <FieldShell label="Company name">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.companyName} onChange={(e) => updateField('companyName', e.target.value)} data-testid="mi-company" />
              </FieldShell>
              <FieldShell label="VAT number">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.customerVatNumber} onChange={(e) => updateField('customerVatNumber', e.target.value)} data-testid="mi-vat" />
              </FieldShell>
              <FieldShell label="PO number">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.purchaseOrderNumber} onChange={(e) => updateField('purchaseOrderNumber', e.target.value)} data-testid="mi-po" />
              </FieldShell>
              <FieldShell label="Cost reference">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.costReference} onChange={(e) => updateField('costReference', e.target.value)} data-testid="mi-costref" />
              </FieldShell>
              <FieldShell label="Invoice contact name" className="col-span-2">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.invoiceContactName} onChange={(e) => updateField('invoiceContactName', e.target.value)} data-testid="mi-contact" />
              </FieldShell>
              <FieldShell label="Billing address" className="col-span-2">
                <Textarea rows={2} className="border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent resize-none" value={manualForm.billingAddress} onChange={(e) => updateField('billingAddress', e.target.value)} data-testid="mi-address" />
              </FieldShell>
            </div>

            {/* === Journey ============================================= */}
            <SectionLabel n="03" title="Journey" />
            <div className="grid grid-cols-2 gap-x-5 gap-y-4">
              <FieldShell label="Service type">
                <Select value={manualForm.serviceDescription} onValueChange={(v) => updateField('serviceDescription', v)}>
                  <SelectTrigger className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus:ring-0 focus:border-black bg-transparent [&>span]:font-medium" data-testid="mi-service-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldShell>
              <FieldShell label="Vehicle">
                <Select value={manualForm.vehicleName} onValueChange={(v) => updateField('vehicleName', v)}>
                  <SelectTrigger className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus:ring-0 focus:border-black bg-transparent [&>span]:font-medium" data-testid="mi-vehicle-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLES.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{v.name}</span>
                          <span className="text-[11px] text-gray-500">{v.meta}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldShell>

              <FieldShell label="Pickup location" icon={MapPin} className="col-span-2 md:col-span-1">
                {gmapsLoaded ? (
                  <Autocomplete onLoad={setPickupAC} onPlaceChanged={onPickupPlace} options={{ componentRestrictions: { country: 'gb' } }}>
                    <Input
                      className="h-11 border-0 border-b border-gray-300 rounded-none pl-6 focus-visible:ring-0 focus-visible:border-black bg-transparent"
                      placeholder="Start typing address…"
                      value={manualForm.pickupLocation}
                      onChange={(e) => updateField('pickupLocation', e.target.value)}
                      data-testid="mi-pickup"
                    />
                  </Autocomplete>
                ) : (
                  <Input
                    className="h-11 border-0 border-b border-gray-300 rounded-none pl-6 focus-visible:ring-0 focus-visible:border-black bg-transparent"
                    placeholder="Pickup location"
                    value={manualForm.pickupLocation}
                    onChange={(e) => updateField('pickupLocation', e.target.value)}
                    data-testid="mi-pickup"
                  />
                )}
              </FieldShell>

              <FieldShell label="Drop-off location" icon={MapPin} className="col-span-2 md:col-span-1">
                {gmapsLoaded ? (
                  <Autocomplete onLoad={setDropoffAC} onPlaceChanged={onDropoffPlace} options={{ componentRestrictions: { country: 'gb' } }}>
                    <Input
                      className="h-11 border-0 border-b border-gray-300 rounded-none pl-6 focus-visible:ring-0 focus-visible:border-black bg-transparent"
                      placeholder="Start typing address…"
                      value={manualForm.dropoffLocation}
                      onChange={(e) => updateField('dropoffLocation', e.target.value)}
                      data-testid="mi-dropoff"
                    />
                  </Autocomplete>
                ) : (
                  <Input
                    className="h-11 border-0 border-b border-gray-300 rounded-none pl-6 focus-visible:ring-0 focus-visible:border-black bg-transparent"
                    placeholder="Drop-off location"
                    value={manualForm.dropoffLocation}
                    onChange={(e) => updateField('dropoffLocation', e.target.value)}
                    data-testid="mi-dropoff"
                  />
                )}
              </FieldShell>

              <FieldShell label="Pickup date">
                <Input type="date" className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.pickupDate} onChange={(e) => updateField('pickupDate', e.target.value)} data-testid="mi-date" />
              </FieldShell>
              <FieldShell label="Pickup time">
                <Input type="time" className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.pickupTime} onChange={(e) => updateField('pickupTime', e.target.value)} data-testid="mi-time" />
              </FieldShell>
              <FieldShell label="Hours (As Directed)">
                <Input type="number" min="0" step="0.5" placeholder="e.g. 9" className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.hours} onChange={(e) => { const h = e.target.value; updateField('hours', h); if (manualForm.pickupTime && h) updateField('endTime', computeEndTime(manualForm.pickupTime, h) || ''); }} data-testid="mi-hours" />
              </FieldShell>
              <FieldShell label="End time">
                <Input type="time" className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.endTime} onChange={(e) => updateField('endTime', e.target.value)} data-testid="mi-endtime" />
              </FieldShell>
              <FieldShell label="Passenger name" className="col-span-2">
                <Input className="h-11 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent" value={manualForm.passengerName} onChange={(e) => updateField('passengerName', e.target.value)} data-testid="mi-passenger" />
              </FieldShell>
            </div>

            {/* === Line items =========================================== */}
            <SectionLabel n="04" title="Line items" />
            <div className="space-y-3" data-testid="mi-line-items">
              {lineItems.map((it, i) => (
                <div key={i} className="border border-gray-200 p-4 space-y-3.5 bg-white" data-testid={`mi-line-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5">Description</label>
                      <Input
                        placeholder="e.g. As Directed Service — 9 hours" value={it.description}
                        onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                        className="h-10 border-0 border-b border-gray-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-black bg-transparent"
                        data-testid={`mi-line-desc-${i}`}
                      />
                    </div>
                    <button
                      type="button" onClick={() => removeLineItem(i)}
                      className="text-gray-300 hover:text-red-600 mt-6 shrink-0"
                      aria-label="Remove line"
                      data-testid={`mi-line-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5">Qty</label>
                      <Input
                        type="number" min="0" step="0.5" placeholder="1" value={it.quantity}
                        onChange={(e) => updateLineItem(i, 'quantity', e.target.value)}
                        className="h-10 border-0 border-b border-gray-300 rounded-none px-0 text-right focus-visible:ring-0 focus-visible:border-black bg-transparent tabular-nums"
                        data-testid={`mi-line-qty-${i}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5">Unit</label>
                      <Select value={it.unit || 'none'} onValueChange={(v) => updateLineItem(i, 'unit', v === 'none' ? '' : v)}>
                        <SelectTrigger className="h-10 border-0 border-b border-gray-300 rounded-none px-0 focus:ring-0 focus:border-black bg-transparent text-sm" data-testid={`mi-line-unit-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_OPTIONS.map((u) => (
                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5">Rate £</label>
                      <Input
                        type="number" min="0" step="0.01" placeholder="0.00" value={it.unitPrice}
                        onChange={(e) => updateLineItem(i, 'unitPrice', e.target.value)}
                        className="h-10 border-0 border-b border-gray-300 rounded-none px-0 text-right focus-visible:ring-0 focus-visible:border-black bg-transparent tabular-nums"
                        data-testid={`mi-line-rate-${i}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-400 mb-1.5 text-right">Amount</label>
                      <div className="h-10 flex items-center justify-end border-b border-transparent">
                        <span className="text-base font-bold tabular-nums" data-testid={`mi-line-amount-${i}`}>£{lineAmount(it).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button" onClick={addLineItem}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-black hover:text-gray-600 mt-1 uppercase tracking-[0.1em]"
                data-testid="mi-add-line"
              >
                <Plus className="h-3.5 w-3.5" /> Add line item
              </button>
              <div className="flex items-center justify-end gap-8 border-t-2 border-black mt-4 pt-4">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-gray-500">Total Due</span>
                <span className="text-3xl font-bold tabular-nums" data-testid="mi-line-total">£{lineItemsTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Sticky footer with hairline */}
          <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-8 py-4 flex items-center justify-between gap-4">
            <label className="flex items-center gap-2.5 text-xs text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox" checked={manualForm.sendEmail}
                onChange={(e) => updateField('sendEmail', e.target.checked)}
                className="h-4 w-4 accent-black"
                data-testid="mi-send-email"
              />
              <span>Email PDF invoice to the customer on creation</span>
            </label>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setShowManual(false)} data-testid="mi-cancel" className="text-gray-600 hover:text-black hover:bg-transparent h-10 px-4 text-xs uppercase tracking-[0.15em]">
                Cancel
              </Button>
              {editingId ? (
                <>
                  <Button onClick={() => submitManual(false)} disabled={submitting} variant="outline" className="border-black text-black hover:bg-black hover:text-white rounded-none h-10 px-5 text-xs uppercase tracking-[0.15em] font-semibold" data-testid="mi-save-draft">
                    Save Draft
                  </Button>
                  <Button onClick={() => submitManual(true)} disabled={submitting} className="bg-black text-white hover:bg-gray-800 rounded-none h-10 px-6 text-xs uppercase tracking-[0.15em] font-semibold" data-testid="mi-save-send">
                    {submitting ? 'Working…' : 'Save & Send'}
                    <Send className="h-3.5 w-3.5 ml-2" />
                  </Button>
                </>
              ) : (
                <Button onClick={() => submitManual(false)} disabled={submitting} className="bg-black text-white hover:bg-gray-800 rounded-none h-10 px-6 text-xs uppercase tracking-[0.15em] font-semibold" data-testid="mi-submit">
                  {submitting ? 'Creating…' : 'Generate Invoice'}
                  <Send className="h-3.5 w-3.5 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default Invoices;
